import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AumicoCoreProvider } from '@modeso/aumico-lib-core-fe';
import { IPersonDTO } from '@modeso/types__aumico-lib-companies-be';
import Debug from 'debug';
import { Observable, finalize } from 'rxjs';
import { AumicoCompaniesConfigService } from '../aumico-companies.config';
import { IPersonExportDto } from '../dtos/person-export-dto.interface';
import { IPersonExportTableHeaders } from '../dtos/person-export-table-header-dto.interface';
import { TasksUpdate } from '../enums/tasks-update.enum';
const debug = Debug('modeso:aumico-companies:AumicoPersonsService');

const httpOptions = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json',
    }),
};

/**
 * One entry of `PUT /persons/bulk-update`: the person plus only what changes —
 * Mandatsleiter, Sachbearbeiter (each with its "zuständig seit") or `active: false` (deactivate).
 */
export type PersonBulkUpdate = Pick<IPersonDTO, '_id'> &
    Partial<Pick<IPersonDTO, 'assignee' | 'assignedOn' | 'clerk' | 'assignedOnClerk' | 'active'>>;

/**
 * REV-22155 — HTTP calls for persons. Endpoints live in aumico-ms-companies under
 * `/persons`; the Excel export in aumico-be (`exporting/persons-xlsx`).
 */
@Injectable({
    providedIn: 'root',
})
export class AumicoPersonsService {
    private server = '';
    private readonly personsUrl: string;

    constructor(
        private http: HttpClient,
        @Inject(AumicoCompaniesConfigService) private config,
        private coreProvider: AumicoCoreProvider,
    ) {
        this.server = config.apiGateway;
        this.personsUrl = `${this.server}persons`;
    }

    /** All persons of the organization; with `companyId` only those linked to that company. */
    getPersons(companyId?: string): Observable<IPersonDTO[]> {
        debug('getPersons called');
        const params = companyId ? new HttpParams().append('companyId', companyId) : undefined;
        this.coreProvider.dispatchBlockingRequest('getPersons');
        return this.http
            .get<IPersonDTO[]>(this.personsUrl, { params })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getPersons')));
    }

    getPersonById(id: string): Observable<IPersonDTO> {
        debug('getPersonById called');
        this.coreProvider.dispatchBlockingRequest('getPersonById');
        return this.http
            .get<IPersonDTO>(`${this.personsUrl}/${id}`)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getPersonById')));
    }

    createPerson(person: IPersonDTO): Observable<IPersonDTO> {
        debug('createPerson called');
        this.coreProvider.dispatchBlockingRequest('createPerson');
        return this.http
            .post<IPersonDTO>(this.personsUrl, person, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('createPerson')));
    }

    /** The whole form; the body is the person itself (not wrapped like the company update). */
    /** `tasksUpdate` sits flat beside the person fields (as `UpdatePersonRequest` expects). */
    updatePerson(person: IPersonDTO, tasksUpdate?: TasksUpdate): Observable<IPersonDTO> {
        debug('updatePerson called');
        const { _id, ...fields } = person;
        const body = tasksUpdate ? { ...fields, tasksUpdate } : fields;
        this.coreProvider.dispatchBlockingRequest('updatePerson');
        return this.http
            .put<IPersonDTO>(`${this.personsUrl}/${_id}`, body, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updatePerson')));
    }

    updatePersonStatus(id: string, active: boolean): Observable<IPersonDTO> {
        debug('updatePersonStatus called');
        this.coreProvider.dispatchBlockingRequest('updatePersonStatus');
        return this.http
            .put<IPersonDTO>(`${this.personsUrl}/${id}/status`, { active }, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updatePersonStatus')));
    }

    /** Deletes for good; the backend answers 204 without a body. */
    deletePerson(id: string): Observable<void> {
        debug('deletePerson called');
        this.coreProvider.dispatchBlockingRequest('deletePerson');
        return this.http
            .delete<void>(`${this.personsUrl}/${id}`, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('deletePerson')));
    }

    /** `tasksUpdate` only with a Sachbearbeiter change: which tasks follow (top-level, like `BulkUpdatePersonRequest`). */
    bulkUpdatePersons(persons: PersonBulkUpdate[], tasksUpdate?: TasksUpdate): Observable<IPersonDTO[]> {
        debug('bulkUpdatePersons called');
        this.coreProvider.dispatchBlockingRequest('bulkUpdatePersons');
        return this.http
            .put<IPersonDTO[]>(`${this.personsUrl}/bulk-update`, tasksUpdate ? { persons, tasksUpdate } : { persons }, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('bulkUpdatePersons')));
    }

    exportPersonsDataAsExcel$(payload: {
        tableHeaders: IPersonExportTableHeaders;
        persons: IPersonExportDto[];
    }): Observable<Blob> {
        const exportUrl = `${this.server}exporting/persons-xlsx`;
        this.coreProvider.dispatchBlockingRequest('exportPersonsDataAsExcel$');
        return this.http
            .post(exportUrl, payload, { responseType: 'blob', observe: 'body' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('exportPersonsDataAsExcel$')));
    }
}
